import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { useT } from '../lib/i18n';
import { Star, Flame, TrendingUp, Plus, Trash2, Trophy, Pencil, Check, Clock, Pin, PinOff, Loader2, RotateCcw, Bot as BotIcon, ChevronRight, BookOpen, FlaskConical } from 'lucide-react';
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell } from 'recharts';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const scoreLabel = (t, acc) => acc >= 85 ? t('great') : acc >= 60 ? t('good') : t('needsPractice');
const scoreColor = (acc) => acc >= 85 ? 'text-green-600 bg-green-50' : acc >= 60 ? 'text-sky-600 bg-sky-50' : 'text-orange-600 bg-orange-50';

const TIMEFRAMES = [
  { v: 'biweekly', label: 'Biweekly (14 days)' },
  { v: 'monthly',  label: 'Monthly (~30 days)' },
  { v: 'custom',   label: 'Custom date' },
];

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const target = new Date(iso.slice(0, 10) + 'T23:59:59');
    const now = new Date();
    return Math.ceil((target - now) / 86400000);
  } catch { return null; }
}

function todayPlus(days) {
  const d = new Date(Date.now() + days * 86400000);
  return d.toISOString().slice(0, 10);
}

export default function Dashboard() {
  const { activeChild, uiLang } = useApp();
  const nav = useNavigate();
  const t = useT(uiLang);
  const [dash, setDash] = useState(null);

  // New-reward form
  const [rTitle, setRTitle] = useState('');
  const [rTarget, setRTarget] = useState('');
  const [rTimeframe, setRTimeframe] = useState('monthly');
  const [rCustomDate, setRCustomDate] = useState(todayPlus(30));

  // Edit-reward modal state
  const [editReward, setEditReward] = useState(null); // reward object or null

  // Iter-51.45 — reading-session edit/trial/delete state moved to
  // /reading-history ("View all"). The Dashboard front card only
  // renders a compact preview now.

  const load = async () => {
    const { data } = await api.get(`/children/${activeChild.child_id}/dashboard`);
    setDash(data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeChild) load(); }, [activeChild]);

  if (!dash) return <div className="p-8 text-center text-slate-500">Loading…</div>;

  const addReward = async () => {
    if (!rTitle || !rTarget) return;
    const body = {
      child_id: activeChild.child_id,
      title: rTitle,
      target_points: Number(rTarget),
      timeframe: rTimeframe,
    };
    if (rTimeframe === 'custom') body.deadline_at = rCustomDate;
    try {
      await api.post('/rewards', body);
      setRTitle(''); setRTarget(''); setRTimeframe('monthly'); setRCustomDate(todayPlus(30));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add reward'); }
  };

  const delReward = async (id) => {
    if (!window.confirm('Delete this reward?')) return;
    await api.delete(`/rewards/${id}`); load();
  };

  const featureReward = async (r) => {
    // Toggle: if this is the featured one, unfeature; else make it featured.
    const nextVal = !r.featured;
    await api.put(`/rewards/${r.reward_id}`, { featured: nextVal });
    load();
  };

  // Iter-51.45 — session-level edit/trial/delete moved to
  // /reading-history. Dashboard front card is now preview-only.

  const totalPoints = dash.total_points || 0;
  const featuredExists = dash.rewards.some(r => r.featured);

  return (
    <div className="px-6 pt-6 pb-36 max-w-5xl mx-auto space-y-6 overflow-x-hidden">
      <h1 className="text-3xl md:text-4xl font-extrabold break-words">{activeChild.name}'s {t('dashboard')}</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div data-testid="stat-total-stars" className="bg-[#F4B740] rounded-3xl p-5">
          <Star size={28} className="text-amber-500 fill-amber-400 mb-2"/>
          <div className="text-xs font-bold uppercase text-slate-500">{t('totalStars')}</div>
          <div className="text-3xl font-extrabold">{totalPoints}</div>
        </div>
        <div data-testid="stat-streak" className="bg-[#F4B740] rounded-3xl p-5">
          <Flame size={28} className="text-orange-500 fill-orange-300 mb-2"/>
          <div className="text-xs font-bold uppercase text-slate-500">{t('streak')}</div>
          <div className="text-3xl font-extrabold">{dash.streak}</div>
        </div>
        <div data-testid="stat-today" className="bg-[#D97398] rounded-3xl p-5">
          <div className="text-xs font-bold uppercase text-slate-500">{t('todayActivity')}</div>
          <div className="text-2xl font-extrabold mt-2">{dash.today.sessions} {t('sessions')}</div>
          <div className="text-sm font-bold text-slate-600">{dash.today.accuracy}% · +{dash.today.points} pts</div>
        </div>
        <div data-testid="stat-week" className="bg-[#D97398] rounded-3xl p-5">
          <TrendingUp size={28} className="text-sky-600 mb-2"/>
          <div className="text-xs font-bold uppercase text-slate-500">{t('weekActivity')}</div>
          <div className="text-2xl font-extrabold">{dash.week.sessions} · +{dash.week.points}</div>
        </div>
      </div>

      {dash.trend?.messages?.length > 0 && (
        <div data-testid="trend-panel" className="bg-white rounded-3xl p-6">
          <h3 className="text-lg font-extrabold mb-3 flex items-center gap-2"><TrendingUp size={20} className="text-emerald-500"/> {t('trendTitle')}</h3>
          <div className="space-y-2">
            {dash.trend.messages.map((m, i) => (
              <div key={i} data-testid={`trend-msg-${i}`} className={`rounded-2xl p-3 font-bold ${m.delta > 0 ? 'bg-green-50 text-green-800' : 'bg-orange-50 text-orange-800'}`}>
                {m.delta > 0 ? '📈 ' : '📉 '}{m.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Iter-51.26 — Beat the Bot activity card. Always renders so
          new players see the empty state + CTA; existing players
          see best score / bots defeated / recent runs at a glance.
          Iter-51.45 — recent rows now use the same compact preview
          pattern as Recent Sessions: date block, book+time, and a
          single "Details" link. Score badge lives in /quiz-history. */}
      <div data-testid="dashboard-beat-the-bot-card" className="bg-white rounded-3xl p-6">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <h3 className="text-lg font-extrabold text-[#0B2545] flex items-center gap-2">
            <BotIcon size={20} className="text-[#1E9E6C]"/> Beat the Bot
            {dash?.beat_the_bot?.sparky_slayer && (
              <span data-testid="dashboard-sparky-slayer-badge"
                className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#FF6B1A] to-[#F4B740] text-white text-[10px] font-extrabold px-2 py-0.5">
                🏆 Sparky-slayer
              </span>
            )}
          </h3>
          <button
            data-testid="dashboard-btb-history-link"
            onClick={() => nav('/quiz-history')}
            className="text-xs font-extrabold text-[#FF6B1A] inline-flex items-center gap-1"
          >
            View all <ChevronRight size={14}/>
          </button>
        </div>
        <p data-testid="dashboard-btb-caption" className="text-xs text-slate-500 mb-4">
          Score is in view all
        </p>
        {(() => {
          const btb = dash.beat_the_bot || {};
          const played = btb.games_played || 0;
          if (played === 0) {
            return (
              <div data-testid="dashboard-btb-empty" className="text-center py-4">
                <p className="text-sm font-bold text-slate-500 mb-3">
                  No quizzes played yet. Open a book, read a chunk, then tap Beat the Bot.
                </p>
                <button
                  data-testid="dashboard-btb-empty-cta"
                  onClick={() => nav('/books')}
                  className="pushable bg-[#1E9E6C] text-white font-extrabold px-4 py-2 rounded-full text-sm inline-flex items-center gap-2"
                >
                  <BotIcon size={14} strokeWidth={3}/> Open a book
                </button>
              </div>
            );
          }
          return (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <StatTile data-testid="btb-stat-played" label="Games played" value={played}/>
                <StatTile data-testid="btb-stat-best" label="Best score" value={btb.best_score_label || '—'} sub={btb.best_score_pct ? `${btb.best_score_pct}%` : ''}/>
                <StatTile data-testid="btb-stat-defeated" label="Bots defeated" value={btb.bots_defeated || 0}/>
                <StatTile data-testid="btb-stat-winrate" label="Recent win rate" value={`${btb.recent_win_rate || 0}%`} sub="last 5"/>
              </div>
              {btb.recent?.length > 0 && (
                <div className="space-y-2">
                  {/* Iter-51.42 — defensive frontend sort by created_at
                      desc so a legacy row with a missing/malformed
                      timestamp can't sink out of chronological order. */}
                  {[...btb.recent].sort((a, b) => {
                    const ta = a?.created_at ? Date.parse(a.created_at) || 0 : 0;
                    const tb = b?.created_at ? Date.parse(b.created_at) || 0 : 0;
                    return tb - ta;
                  }).map(r => {
                    // Iter-51.45 — compact preview: date block, title,
                    // time, Details link. No score on the front card.
                    const played = r.created_at ? new Date(r.created_at) : null;
                    const day = played ? played.getDate() : '';
                    const mon = played ? played.toLocaleDateString(undefined, { month: 'short' }) : '';
                    const timeStr = played ? played.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '';
                    return (
                      <div
                        key={r.run_id}
                        data-testid={`btb-recent-${r.run_id}`}
                        className={
                          'flex items-center bg-slate-50 rounded-2xl px-3 py-2.5 gap-3 ' +
                          (r.is_trial ? 'opacity-70' : '')
                        }
                      >
                        <div
                          data-testid={`btb-recent-date-block-${r.run_id}`}
                          className="w-12 text-center bg-white rounded-xl border border-slate-200 py-1 flex-shrink-0"
                        >
                          <div className="text-lg font-extrabold text-[#0B2545] leading-none">{day}</div>
                          <div className="text-[10px] font-extrabold uppercase text-slate-500 tracking-wider mt-0.5">{mon}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-extrabold text-sm text-[#0B2545] truncate">{r.book_title || 'Beat the Bot'}</div>
                          <div className="text-[11px] font-bold text-slate-500 truncate">
                            {r.quiz_type === 'spelling' ? 'Spelling' : 'Knowledge'} · {r.language === 'hi' ? 'Hindi' : 'English'}
                          </div>
                          {/* Iter-51.48 — Tournament vs Quick label. */}
                          {(() => {
                            const isT = r.mode === 'tournament';
                            const res = r.tournament_result;
                            const beat = r.bots_beaten || 0;
                            const orig = Array.isArray(r.bots) ? r.bots.length : 1;
                            const label = isT
                              ? (res === 'won'
                                  ? `Tournament · Won vs ${orig}`
                                  : res === 'lost'
                                    ? `Tournament · Beat ${beat}/${orig}`
                                    : res === 'sparky_tie'
                                      ? 'Tournament · Sparky tie'
                                      : 'Tournament')
                              : (res === 'sparky_tie' ? 'Quick · Sparky tie' : 'Quick Challenge');
                            return (
                              <div
                                data-testid={`btb-recent-mode-${r.run_id}`}
                                className={
                                  'text-[10px] font-extrabold uppercase tracking-wider mt-0.5 truncate ' +
                                  (isT ? 'text-[#5B8DEF]' : 'text-slate-500')
                                }
                              >
                                {label}
                              </div>
                            );
                          })()}
                          <div
                            data-testid={`btb-recent-time-${r.run_id}`}
                            className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mt-0.5"
                          >
                            {timeStr}
                          </div>
                        </div>
                        <button
                          data-testid={`btb-recent-details-${r.run_id}`}
                          onClick={() => nav(`/quiz-review/${r.run_id}`)}
                          className="text-xs font-extrabold text-[#FF6B1A] inline-flex items-center gap-0.5 flex-shrink-0 px-2 py-1 rounded-full hover:bg-white"
                          aria-label="Open quiz details"
                        >
                          Details <ChevronRight size={12}/>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          );
        })()}
      </div>

      <div className="bg-white rounded-3xl p-6">
        <h3 className="text-lg font-extrabold mb-4">{t('weekActivity')}</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={dash.week.bars}>
            <XAxis dataKey="day" tickLine={false} axisLine={false} tick={{ fontWeight: 700, fill: '#64748B', fontSize: 13 }}/>
            <Bar dataKey="sessions" radius={[12, 12, 12, 12]}>
              {dash.week.bars.map((_, i) => <Cell key={i} fill="#FF6B1A"/>)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {dash.top_missed.length > 0 && (
        <div className="bg-white rounded-3xl p-6">
          <h3 className="text-lg font-extrabold mb-4">{t('missedWords')}</h3>
          <div className="flex flex-wrap gap-2">
            {dash.top_missed.map((m, i) => (
              <span key={i} className={`px-4 py-2 rounded-full font-bold text-sm ${scoreColor(0)} ${activeChild.languages?.includes('hi') ? 'font-hi' : ''}`}>
                {m.word} <span className="opacity-60">×{m.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Rewards */}
      <div className="bg-white rounded-3xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-extrabold flex items-center gap-2"><Trophy size={20} className="text-amber-500"/> {t('rewards')}</h3>
        </div>
        <div className="space-y-3 mb-4">
          {dash.rewards.map(r => {
            const status = r.status || 'active';
            const progress = r.progress ?? Math.min(r.target_points, Math.max(0, totalPoints - (r.starting_points || 0)));
            const pct = Math.min(100, Math.round((progress / Math.max(1, r.target_points)) * 100));
            const days = daysUntil(r.deadline_at);
            const isExpired = status === 'expired';
            const isCompleted = status === 'completed';
            return (
              <div data-testid={`reward-${r.reward_id}`} key={r.reward_id}
                className={`rounded-2xl p-4 border-2 ${isCompleted ? 'bg-green-50 border-green-200' : isExpired ? 'bg-slate-100 border-slate-200 opacity-70' : r.featured ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-100'}`}>
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-extrabold flex items-center gap-2 flex-wrap">
                      <span className="truncate">{r.title}</span>
                      {isCompleted && <span data-testid={`reward-tag-completed-${r.reward_id}`} className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full">Unlocked 🎉</span>}
                      {isExpired && <span data-testid={`reward-tag-expired-${r.reward_id}`} className="text-xs bg-slate-500 text-white px-2 py-0.5 rounded-full">Deadline passed</span>}
                      {r.featured && !isCompleted && !isExpired && <span data-testid={`reward-tag-featured-${r.reward_id}`} className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Pin size={10}/>Featured</span>}
                    </div>
                    {r.description && <div className="text-sm text-slate-500">{r.description}</div>}
                    <div className="text-xs font-bold text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                      <span>{progress} / {r.target_points} pts · {pct}%</span>
                      {r.deadline_at && !isCompleted && (
                        <span className="inline-flex items-center gap-1"><Clock size={11}/>
                          {days < 0 ? 'Deadline passed' : days === 0 ? 'Ends today' : `${days} day${days === 1 ? '' : 's'} left`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button data-testid={`feature-reward-${r.reward_id}`} onClick={() => featureReward(r)}
                      title={r.featured ? 'Unpin from Home' : 'Feature on Home'}
                      className={`p-2 rounded-full ${r.featured ? 'bg-amber-500 text-white' : 'hover:bg-white text-slate-400'}`}>
                      {r.featured ? <PinOff size={14}/> : <Pin size={14}/>}
                    </button>
                    <button data-testid={`edit-reward-${r.reward_id}`} onClick={() => setEditReward(r)} className="p-2 rounded-full hover:bg-white text-slate-500">
                      <Pencil size={14}/>
                    </button>
                    <button data-testid={`del-reward-${r.reward_id}`} onClick={() => delReward(r.reward_id)} className="p-2 rounded-full hover:bg-white">
                      <Trash2 size={14} className="text-slate-400"/>
                    </button>
                  </div>
                </div>
                <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${isCompleted ? 'bg-green-500' : isExpired ? 'bg-slate-400' : 'bg-[#FF6B1A]'}`} style={{ width: `${pct}%` }}/>
                </div>
              </div>
            );
          })}
        </div>

        {/* New reward form */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
          <input data-testid="reward-title-input" value={rTitle} onChange={e => setRTitle(e.target.value)} placeholder={t('rewardTitle')} className="px-4 py-3 rounded-2xl border-2 border-slate-200 font-bold" spellCheck={false}/>
          <input data-testid="reward-target-input" value={rTarget} onChange={e => setRTarget(e.target.value)} placeholder={t('rewardTarget')} type="number" className="px-4 py-3 rounded-2xl border-2 border-slate-200 font-bold"/>
          <select data-testid="reward-timeframe-select" value={rTimeframe} onChange={e => setRTimeframe(e.target.value)} className="px-4 py-3 rounded-2xl border-2 border-slate-200 font-bold">
            {TIMEFRAMES.map(tf => <option key={tf.v} value={tf.v}>{tf.label}</option>)}
          </select>
          {rTimeframe === 'custom' ? (
            <input data-testid="reward-custom-date-input" type="date" value={rCustomDate} onChange={e => setRCustomDate(e.target.value)} className="px-4 py-3 rounded-2xl border-2 border-slate-200 font-bold"/>
          ) : (
            <button data-testid="add-reward-btn" onClick={addReward} className="pushable pushable-primary bg-[#FF6B1A] text-white font-bold px-6 py-3 rounded-full inline-flex items-center justify-center gap-2">
              <Plus size={20} strokeWidth={3}/> {t('addReward')}
            </button>
          )}
          {rTimeframe === 'custom' && (
            <button data-testid="add-reward-btn" onClick={addReward} className="pushable pushable-primary bg-[#FF6B1A] text-white font-bold px-6 py-3 rounded-full inline-flex items-center justify-center gap-2 sm:col-span-4">
              <Plus size={20} strokeWidth={3}/> {t('addReward')}
            </button>
          )}
        </div>
        {!featuredExists && dash.rewards.some(r => (r.status || 'active') === 'active') && (
          <p className="text-xs text-slate-500 mt-2">Tip: tap the pin icon on a reward to feature it on Home &amp; the profile-select screen.</p>
        )}
      </div>

      {/* Iter-51.45 — Recent Sessions compact preview. Matches the
          Beat-the-Bot card visual pattern: chevron "View all" header,
          caption under it, then compact rows (date block, book +
          chunk, time, language, single "Details" button). Score
          badge + edit/flask/delete actions moved to /reading-history. */}
      <div data-testid="dashboard-recent-sessions-card" className="bg-white rounded-3xl p-6">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <h3 className="text-lg font-extrabold text-[#0B2545] flex items-center gap-2">
            <BookOpen size={20} className="text-[#FF6B1A]"/> Recent sessions
          </h3>
          <button
            data-testid="dashboard-reading-history-link"
            onClick={() => nav('/reading-history')}
            className="text-xs font-extrabold text-[#FF6B1A] inline-flex items-center gap-1"
          >
            View all <ChevronRight size={14}/>
          </button>
        </div>
        <p data-testid="dashboard-recent-sessions-caption" className="text-xs text-slate-500 mb-4">
          Score and edit options are in view all
        </p>
        <div className="space-y-2">
          {dash.recent_sessions.length === 0 && <p className="text-slate-500">No sessions yet.</p>}
          {dash.recent_sessions.map(s => {
            const played = s.created_at ? new Date(s.created_at) : null;
            const day = played ? played.getDate() : '';
            const mon = played ? played.toLocaleDateString(undefined, { month: 'short' }) : '';
            const timeStr = played ? played.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '';
            const chunkNo = typeof s.chunk_index === 'number' ? s.chunk_index + 1 : null;
            const title = s.book_title || (s.original_text || '').slice(0, 40) || 'Reading session';
            return (
              <div
                key={s.session_id}
                data-testid={`session-row-${s.session_id}`}
                className={
                  'flex items-center bg-slate-50 rounded-2xl px-3 py-2.5 gap-3 ' +
                  (s.is_trial ? 'opacity-70' : '')
                }
              >
                {/* Date block, left */}
                <div
                  data-testid={`session-date-block-${s.session_id}`}
                  className="w-12 text-center bg-white rounded-xl border border-slate-200 py-1 flex-shrink-0"
                >
                  <div className="text-lg font-extrabold text-[#0B2545] leading-none">{day}</div>
                  <div className="text-[10px] font-extrabold uppercase text-slate-500 tracking-wider mt-0.5">{mon}</div>
                </div>
                {/* Title + chunk / time · lang */}
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-sm text-[#0B2545] truncate flex items-center gap-1.5">
                    <span className="truncate">{s.label || title}</span>
                    {s.is_trial && (
                      <span
                        data-testid={`session-trial-chip-${s.session_id}`}
                        className="text-[9px] font-extrabold uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5 flex-shrink-0"
                      ><FlaskConical size={9} strokeWidth={3}/> Trial</span>
                    )}
                  </div>
                  <div className="text-[11px] font-bold text-slate-500 truncate">
                    {chunkNo ? `chunk ${chunkNo}` : 'passage'}
                  </div>
                  <div
                    data-testid={`session-time-${s.session_id}`}
                    className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mt-0.5"
                  >
                    {timeStr && <span>{timeStr} · </span>}{s.language?.toUpperCase()}
                  </div>
                </div>
                {/* Single "Details" button, right */}
                <button
                  data-testid={`session-details-${s.session_id}`}
                  onClick={() => nav('/reading-history')}
                  className="text-xs font-extrabold text-[#FF6B1A] inline-flex items-center gap-0.5 flex-shrink-0 px-2 py-1 rounded-full hover:bg-white"
                  aria-label="Open reading history for details"
                >
                  Details <ChevronRight size={12}/>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {editReward && (
        <RewardEditModal
          reward={editReward}
          languages={activeChild.languages}
          onClose={() => setEditReward(null)}
          onSaved={() => { setEditReward(null); load(); }}
        />
      )}

    </div>
  );
}
// -------------------------------------------------------------------------
// Small stat tile used by the Beat-the-Bot card (iter-51.26).
// -------------------------------------------------------------------------
function StatTile({ label, value, sub, ...rest }) {
  return (
    <div {...rest} className="rounded-2xl bg-[#FFF3E9] border-2 border-[#FF6B1A]/20 p-3">
      <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-xl font-extrabold text-[#2B2140] tabular-nums">{value}</div>
      {sub && <div className="text-[10px] font-bold text-slate-400">{sub}</div>}
    </div>
  );
}

// -------------------------------------------------------------------------
// Reward edit modal — inline component (small, single-use).
// -------------------------------------------------------------------------
function RewardEditModal({ reward, languages, onClose, onSaved }) {
  const [title, setTitle] = useState(reward.title || '');
  const [target, setTarget] = useState(String(reward.target_points || ''));
  const [description, setDescription] = useState(reward.description || '');
  const [timeframe, setTimeframe] = useState(reward.timeframe || 'monthly');
  const [deadline, setDeadline] = useState((reward.deadline_at || '').slice(0, 10));
  const [saving, setSaving] = useState(false);
  const isDone = reward.status === 'completed' || reward.status === 'expired';

  const save = async () => {
    if (!title.trim() || !target || Number(target) <= 0) {
      toast.error('Title and a positive target are required');
      return;
    }
    const body = {
      title: title.trim(),
      target_points: Number(target),
      description,
      timeframe,
      deadline_at: deadline || undefined,
    };
    // If the parent is editing an expired/completed reward with a NEW
    // deadline, ask them to confirm the progress reset (spec).
    if (isDone && (deadline !== (reward.deadline_at || '').slice(0, 10) || timeframe !== reward.timeframe)) {
      const ok = window.confirm(
        `Reviving this reward will reset the progress to 0 points. The student will need to earn ${target} points again within the new deadline. Continue?`
      );
      if (!ok) return;
      body.revive = true;
    }
    setSaving(true);
    try {
      await api.put(`/rewards/${reward.reward_id}`, body);
      toast.success('Reward updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update reward');
    } finally { setSaving(false); }
  };

  const revive = async () => {
    const ok = window.confirm(
      `Revive "${reward.title}" and reset progress to 0? The student will need to earn ${reward.target_points} points again within the new deadline.`
    );
    if (!ok) return;
    setSaving(true);
    try {
      await api.put(`/rewards/${reward.reward_id}`, {
        revive: true,
        timeframe,
        deadline_at: deadline || undefined,
      });
      toast.success('Reward revived — progress reset to 0');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not revive reward');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div data-testid="reward-edit-modal" className="bg-white rounded-3xl p-6 max-w-md w-full shadow-xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-extrabold mb-4">Edit reward</h2>
        {isDone && (
          <div data-testid="reward-status-banner" className={`rounded-2xl p-3 mb-4 text-sm font-bold ${reward.status === 'completed' ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-100 text-slate-700'}`}>
            {reward.status === 'completed'
              ? '🎉 This reward has been unlocked. Editing the deadline or timeframe will reset the progress.'
              : '⏰ This reward\'s deadline passed. Revive it below — progress will reset to 0.'}
          </div>
        )}
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Title</label>
            <input data-testid="edit-reward-title-input" value={title} onChange={e => setTitle(e.target.value)} spellCheck={false}
              className="w-full mt-1 px-4 py-3 rounded-2xl border-2 border-slate-200 font-bold"/>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Description (optional)</label>
            <input value={description} onChange={e => setDescription(e.target.value)} spellCheck={false}
              className="w-full mt-1 px-4 py-3 rounded-2xl border-2 border-slate-200 font-bold"/>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Points target</label>
            <input data-testid="edit-reward-target-input" type="number" value={target} onChange={e => setTarget(e.target.value)}
              className="w-full mt-1 px-4 py-3 rounded-2xl border-2 border-slate-200 font-bold"/>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Timeframe</label>
            <select data-testid="edit-reward-timeframe-select" value={timeframe} onChange={e => setTimeframe(e.target.value)}
              className="w-full mt-1 px-4 py-3 rounded-2xl border-2 border-slate-200 font-bold">
              {TIMEFRAMES.map(tf => <option key={tf.v} value={tf.v}>{tf.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Deadline</label>
            <input data-testid="edit-reward-deadline-input" type="date" value={deadline} onChange={e => setDeadline(e.target.value)}
              className="w-full mt-1 px-4 py-3 rounded-2xl border-2 border-slate-200 font-bold"/>
            <p className="text-xs text-slate-500 mt-1">Leave blank to auto-derive from the timeframe.</p>
          </div>
        </div>
        <div className="flex gap-2 mt-6 flex-wrap">
          <button data-testid="edit-reward-cancel" onClick={onClose} className="pushable bg-white border-2 border-slate-200 font-bold px-5 py-3 rounded-full flex-1 min-w-[100px]">Cancel</button>
          {isDone && (
            <button data-testid="edit-reward-revive" onClick={revive} disabled={saving}
              className="pushable bg-amber-500 text-white font-bold px-5 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
              <RotateCcw size={16} strokeWidth={3}/> Revive &amp; reset
            </button>
          )}
          <button data-testid="edit-reward-save" onClick={save} disabled={saving}
            className="pushable pushable-primary bg-[#FF6B1A] text-white font-bold px-5 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60 flex-1 min-w-[100px]">
            {saving ? <Loader2 size={16} className="animate-spin"/> : <Check size={16} strokeWidth={3}/>} Save
          </button>
        </div>
      </div>
    </div>
  );
}
